import { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import NavBar from '../../components/NavBar';
import { ROUTES } from '../../defs/routes';
import SelectPassengers from '../order/select_passengers';
import { UserContext } from '../../store/UserProvider';
import ExcursionInfo from '../order/excursion_info';
import { RepositoryContext } from '../../store/RepositoryProvider';
import Schedule from '../../models/schedule/Schedule';
import API from '../../server/api';
import DateFormat from '../../components/DateFormat';
import { getDatePeriodArray } from '../../components/DatePeriod';
import Table from '../../components/Table';
import Price, { CURRENCY } from '../../components/Price';
import log from '../../utils/logger';
import QuestionIcon from '../../components/icons/Question';
import Input from '../../components/Input';
import SearchIcon from '../../components/icons/Search';
import Field from '../../components/Field';
import { useOrderSession } from '../../hooks/useLocalStorage';
import OrderSession from '../../models/session/OrderSession';
import DateTZ from '../../models/date/DateTZ';
import { PriceType } from '../../models/types';
import Route from '../../models/route/Route';
import Badge, {BADGE_TYPE} from "../../components/Badge";

const ExcursionsShared = () => {
  const navigate = useNavigate();
  const {t} = useTranslation(['common', 'excursion', 'order']);

  const [days, setDays] = useState([]);
  const [date, setDate] = useState(null);
  const [excursion, setExcursion] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [price, setPrice] = useState(null);
  const [prices, setPrices] = useState([]);
  const [subdivisionPrices, setSubdivisionPrices] = useState([]);
  const [query, setQuery] = useState('');
  const userInfo = useContext(UserContext);
  const repository = useContext(RepositoryContext);

  const [showSelectPassengers, setShowSelectPassengers] = useState(false);
  const [showExcursionInfo, setShowExcursionInfo] = useState(false);
  const [subdivisionId] = userInfo.useSession('subdivision');

  const [, setOrderSession] = useOrderSession();

  const handleSearch = (_query = query, day = date) => {
    setIsLoading(true);

    let filters = {
      date: day || DateFormat.Request({ date: new DateTZ() }),
      subdivision: subdivisionId
    };

    repository.list(API.excursion.excursions_shared, filters, Schedule).then(async (schedules) => {
      let _schedules = [];
      for(const schedule of schedules) {
          let _excursions = schedule.excursions.filter((excursion) => !_query || (excursion.excursion.title.indexOf(_query) >= 0));

        if (_excursions.length > 0) {
          schedule.excursions = [];

          for(const excursion of _excursions) {
              if (excursion.excursion !== null) {
                  schedule.excursions.push(excursion)

                  for(const subdivision of excursion.excursion.subdivisions) {
                      let minPrices = {
                          [PriceType.NORMAL]: null,
                          [PriceType.CHILD]: null
                      };

                      for(const p of subdivision.prices) {
                          const price = (await repository.getModel('Price', p.price.id)).value;

                          let exPrice = {
                              price,
                              exPrice: p,
                              type: price.priceType,
                              amount: p.amount,
                              count: minPrices[price.priceType] ? minPrices[price.priceType].count + 1 : 1
                          };

                          minPrices[price.priceType] = minPrices[price.priceType] ? (minPrices[price.priceType].amount < exPrice.amount ? minPrices[price.priceType] : exPrice) : exPrice;
                          minPrices[price.priceType].count = exPrice.count;
                      }

                      subdivision.minPrices = minPrices;
                  }
              }
          }
          if (schedule.excursions.length) {
              _schedules.push(schedule);
          }
        }
      }

      setSchedules(_schedules);
      setIsLoading(false);

      let _day = days.find((d) => d.str === day);
      if (_day) {
        _day.count = _day.count ? _day.count + 1 : 1;
      }
    }).catch((e) => {
      log.error(e);
      setSchedules([]);
    });
  };

  const handleCloseSelectPassengers = useCallback(() => {
    setOrderSession(new OrderSession());
    setShowSelectPassengers(false);
  }, []);

  const handleOpenSelectPassengers = useCallback(async (schedule, excursion, subdivision, prices, price) => {
    setSubdivisionPrices(prices);

    let places = [];
    if (schedule.route && schedule.route.id) {
      let route = (await repository.getModel(Route.name, schedule.route.id)).value;
      if (route) {
        let routeSub = route.subdivisions.find((s) => s.subdivision.id === subdivision.id);
        places = routeSub?.places ?? [];
      }
    }

    setOrderSession(new OrderSession({
      date,
      subdivision: subdivision.id,
      time: schedule.getTime(),
      schedule: schedule.id,
      excursion: excursion.id,
      place: places.length ? places[0].place.id : null,
    }));

    setShowSelectPassengers(true);
    setPrice(price);
    setSchedule(schedule);
  }, [date]);

  const handleNextSelectPassengers = useCallback(({passengers, wishes}) => {
    setOrderSession({
      passengers,
      wishes
    });

    navigate(ROUTES.NEW_ORDER);
  }, []);

  const handleSelectScheduleDate = useCallback((day) => {
    let data = {
      date: DateFormat.Request({ date: day }),
      subdivision: subdivisionId
    };
    setDate(data.date);

    setOrderSession(data);

    if (subdivisionId) {
      handleSearch(query, data.date);
    }
  }, []);

  const handleCloseExcursionInfo = useCallback(() => {
    setShowExcursionInfo(false);
    setExcursion(null);
  }, []);

  const handleOpenExcursionInfo = useCallback((excursion) => {
    setExcursion(excursion);
    setShowExcursionInfo(true);
  }, []);


  const fetchSchedules = () => {
    let newDays = getDatePeriodArray(new DateTZ(), 14);
    setDays(newDays);

    setDate(DateFormat.Request({ date: newDays[0].date.toDate() }));
  };

  useEffect(() => {
    if (prices.length > 0) {
      fetchSchedules();
    }
  }, [prices]);

  useEffect(() => {
    if (prices.length > 0 && subdivisionId) {
      handleSearch(query, date);
    }
  }, [query, subdivisionId, prices]);

  useEffect(() => {
    repository.list(API.price.prices, {tab: 'excursion'}).then((prices) => {
      setPrices(prices);
    }).catch((e) => {
    });
  }, []);

  return (
    <div className="trip-app">
        <NavBar title={(
            <>
                <span className={'title-disabled'} onClick={() => navigate(ROUTES.EXCURSIONS, { replace: true })}>
                    {t('excursion:excursions.title')}
                </span>
                <span
                    style={{paddingLeft: '20px'}}
                >
                    {t('excursion:excursions.partners')}
                </span>
            </>
        )} />
      <div className="trip-app-body">
        <Field>
          <Input value={query} icons={<SearchIcon onClick={() => handleSearch(query, date)} />} onChange={setQuery}/>
          <div className="calendar-day-list cards">
            {days.map((day) => (
              <div
                key={day.id}
                className={
                  'card calendar-day' +
                  (day.count ? '' : ' empty-excursion') +
                  (date === day.str ? ' selected' : '')
                }
                onClick={() => handleSelectScheduleDate(day.date.toDate())}
              >
                <div className="day-date">{`${day.title}`}</div>
                <div className={'day-name' + (day.week % 6 === 0 ? ' weekend' : '')}>
                  {t(`common:days.short.day${day.week}`)}
                </div>
              </div>
            ))}
          </div>
        </Field>
        <div className="excursion-list">
          {(!schedules?.length && !isLoading) && (
            <div className="loading-message">
              {t('common:error.not_found')}
            </div>
          )}
          {isLoading && (
            <div className="loading-message">
              {t('common:error.loading')}
            </div>
          )}
          {schedules.map((schedule, index) => (

            schedule.excursions.map((excursion) => (
              excursion.excursion && excursion.subdivisions[0] && excursion.excursion.subdivisions.map((subdivision, i) => (
                (subdivision && !i && (subdivision.subdivision.id) && (
                  <>
                      {(!index || (index > 0 && schedules[index-1].account._id !== schedule.account._id)) && (
                          <Badge type={BADGE_TYPE.INFO} className="mb-2">
                              {schedule.account.title}
                          </Badge>
                      )}

                      <div key={`${schedule.id}_${excursion.id}_${subdivision.id}`}
                           className="card excursion-card"
                           style={{'backgroundImage': `url(${excursion.excursion.cover ? excursion.excursion.cover.getUrl(400) : ''})`}}
                           onClick={() => { handleOpenSelectPassengers(schedule, excursion.excursion, subdivision.subdivision, subdivision.prices, subdivision.minPrices[PriceType.NORMAL].exPrice); }}
                      >
                          <div className="card-header">
                              <h4 className="card-header-name">{excursion.excursion.title}</h4>
                              <div className="card-header-info">
                                  <QuestionIcon className="rounded" onClick={(e) => {e && e.stopPropagation(); handleOpenExcursionInfo(excursion.excursion);}} />
                              </div>
                              <div className="excursion-duration">
                                  {subdivision.getDuration().days > 0 && t('common:duration.day.day', {count: subdivision.getDuration().days})} {subdivision.getDuration().hours > 0 && t('common:duration.hour.hour', {count: subdivision.getDuration().hours})}
                              </div>
                          </div>
                          <div className="collapse show" id="mycard-collapse">
                              <div className="card-body">
                                  <div className="card-subdivision">
                                      <div className="subdivision-time">
                                          {schedule.getTime()}
                                      </div>
                                      <div className="subdivision-places">
                                          {schedule.limit.stopSelling ? (schedule.getSeats() > 0 ? t('common:place.place', {count: schedule.getSeats() || 0}) : t('excursion:excursions.no_places')) : t('excursion:excursions.many_places')}
                                      </div>
                                  </div>
                              </div>
                              <div className="card-footer excursion-tickets">
                                  <Table cols={3}>
                                      <Table.Row>
                                          {subdivision.prices && subdivision.prices.filter((price) => !price.price.deletedAt).length === 0 && (
                                              <div className="excursion-ticket-type">{t('excursion:excursions.no_price')}</div>
                                          )}
                                          {subdivision.minPrices[PriceType.NORMAL] && (
                                              <Table.Cell className="excursion-ticket">
                                                  <div className="excursion-ticket-type">{t(`excursion:price.type.${PriceType.NORMAL}${subdivision.minPrices[PriceType.NORMAL].count > 1 ? '_from' : ''}`)}</div>
                                                  <div className="excursion-ticket-price"><Price currency={CURRENCY.RUB} value={subdivision.minPrices[PriceType.NORMAL].amount} /></div>
                                              </Table.Cell>
                                          )}
                                          {subdivision.minPrices[PriceType.CHILD] && (
                                              <Table.Cell className="excursion-ticket">
                                                  <div className="excursion-ticket-type">{t(`excursion:price.type.${PriceType.CHILD}${subdivision.minPrices[PriceType.CHILD].count > 1 ? '_from' : ''}`)}</div>
                                                  <div className="excursion-ticket-price"><Price currency={CURRENCY.RUB} value={subdivision.minPrices[PriceType.CHILD].amount} /></div>
                                              </Table.Cell>
                                          )}
                                      </Table.Row>
                                  </Table>
                              </div>
                          </div>
                      </div>
                  </>
                ))
              ))
            ))
          ))}
        </div>
      </div>

      <Modal show={showSelectPassengers} onHide={handleCloseSelectPassengers}>
        <Modal.Header closeButton>
          {t('order:select_passengers.title')}
        </Modal.Header>
        <Modal.Body>
          <SelectPassengers
            prices={subdivisionPrices}
            limits={{seats: (schedule ? schedule.limit.value - schedule.limit.cntSeats : 0), stopSelling: schedule ? schedule.limit.stopSelling : false}}
            schedule={schedule}
            passengers={[]}
            wishes={[]}
            onApply={handleNextSelectPassengers}
            onCancel={handleCloseSelectPassengers}
          />
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>

      <Modal show={excursion && showExcursionInfo} onHide={handleCloseExcursionInfo}>
        <Modal.Header closeButton>
          {excursion && excursion.title}
        </Modal.Header>
        <Modal.Body>
          <ExcursionInfo excursion={excursion} prices={prices} onApply={handleCloseExcursionInfo} onClose={handleCloseExcursionInfo} />
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ExcursionsShared;